import React from 'react';
import BiasIndicator from './BiasIndicator';
import ConfidenceGauge from './ConfidenceGauge';
import SupportResistanceChart from './SupportResistanceChart';

const AssetAnalysisCard = ({ asset, data, compact = false }) => {
  if (!data) return null;

  const {
    current_price,
    bias,
    confidence,
    trend,
    support,
    resistance,
    notes
  } = data;

  const assetIcons = {
    BTC: '/images/btc.png',
    AVAX: '/images/avax.png',
    'BTC.D': '/images/btc.png'
  };

  const assetNames = {
    BTC: 'Bitcoin',
    AVAX: 'Avalanche',
    'BTC.D': 'BTC Dominance'
  };

  const formatPrice = (val) => {
    if (!val) return 'N/A';
    const num = parseFloat(val);
    if (asset === 'BTC.D') return `${num.toFixed(2)}%`;
    if (num >= 1000) return `$${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (num >= 1) return `$${num.toFixed(2)}`;
    return `$${num.toFixed(4)}`;
  };

  if (compact) {
    return (
      <div className="asset-card-compact" style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '12px',
        padding: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img
            src={assetIcons[asset]}
            alt={asset}
            style={{ width: '32px', height: '32px', borderRadius: '8px' }}
          />
          <div>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>{asset}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
              {formatPrice(current_price)}
            </div>
          </div>
        </div>
        <BiasIndicator bias={bias} size="small" showLabel={false} />
      </div>
    );
  }

  return (
    <div className="asset-analysis-card" style={{
      background: 'var(--card-background, rgba(26, 26, 46, 0.8))',
      backdropFilter: 'blur(10px)',
      border: '1px solid var(--border-color, rgba(99, 102, 241, 0.2))',
      borderRadius: '16px',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={assetIcons[asset]}
            alt={asset}
            style={{ width: '44px', height: '44px', borderRadius: '12px' }}
          />
          <div>
            <div style={{ fontWeight: '700', fontSize: '18px' }}>{asset}</div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
              {assetNames[asset]}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '20px', fontWeight: '700' }}>{formatPrice(current_price)}</div>
          {trend && (
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
              {trend}
            </div>
          )}
        </div>
      </div>

      {/* Bias and Confidence */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <BiasIndicator bias={bias} size="medium" />
        <ConfidenceGauge value={confidence || 0} size={80} label="Conf." />
      </div>

      {/* Support/Resistance Levels */}
      {(support || resistance) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px'
        }}>
          {support && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
                Support
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#10b981' }}>
                {formatPrice(support)}
              </div>
            </div>
          )}
          {resistance && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
                Resistance
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#ef4444' }}>
                {formatPrice(resistance)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Support/Resistance Chart */}
      {support && resistance && (
        <SupportResistanceChart
          currentPrice={current_price}
          support={support}
          resistance={resistance}
          height={50}
        />
      )}

      {/* Notes */}
      {notes && (
        <div style={{
          fontSize: '13px',
          color: 'rgba(255,255,255,0.7)',
          lineHeight: '1.5',
          padding: '10px',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '8px'
        }}>
          {notes}
        </div>
      )}
    </div>
  );
};

export default AssetAnalysisCard;
